import { supabase } from "../supabase";
import type { FoodEntry } from "../types";

export type FoodContext = { householdId: string; userId: string };

function payload(entry: FoodEntry, context: FoodContext) {
  return {
    household_id: context.householdId,
    home_id: entry.homeId,
    storage_space_id: entry.spaceId,
    barcode: entry.barcode || null,
    name: entry.name.trim(),
    food_group: entry.category || "Other",
    quantity_amount: entry.quantity,
    quantity_unit: entry.unit,
    purchase_date: entry.purchaseDate || null,
    use_by_date: entry.useByDate || null,
    notes: entry.notes.trim() || null,
    created_by: context.userId,
  };
}

export async function createFood(entry: FoodEntry, context: FoodContext) {
  const result = await supabase.rpc("create_food_with_event", { p_item: payload(entry, context) });
  if (result.error) throw result.error;
}

export async function updateFood(id: string, entry: FoodEntry, context: FoodContext) {
  const result = await supabase.rpc("update_food_with_event", { p_food_item: id, p_item: payload(entry, context) });
  if (result.error) throw result.error;
}

export async function deleteFood(id: string) {
  const result = await supabase.rpc("delete_food_with_event", { p_food_item: id });
  if (result.error) throw result.error;
}

export function validateMoveQuantity(moveQuantity: number, availableQuantity: number) {
  if (!Number.isFinite(moveQuantity) || moveQuantity <= 0) throw new Error("Move quantity must be greater than zero.");
  if (moveQuantity > availableQuantity) throw new Error(`Move quantity cannot exceed the available quantity (${availableQuantity}).`);
}

export function validateConsumeQuantity(quantity: number, availableQuantity: number) {
  if (!Number.isFinite(quantity) || quantity <= 0) throw new Error("Consume quantity must be greater than zero.");
  if (quantity > availableQuantity) throw new Error(`Consume quantity cannot exceed the available quantity (${availableQuantity}).`);
}

export async function moveFood(id: string, spaceId: string, moveQuantity: number, availableQuantity: number) {
  validateMoveQuantity(moveQuantity, availableQuantity);
  const result = await supabase.rpc("move_food_quantity", {
    p_food_item: id,
    p_storage_space: spaceId,
    p_quantity: moveQuantity,
  });
  if (result.error) throw result.error;
}

export async function consumeFood(id: string, quantity: number, availableQuantity: number, reason: string) {
  validateConsumeQuantity(quantity, availableQuantity);
  if (!["Ate", "Threw Away", "Donated", "Spoiled"].includes(reason)) throw new Error("Choose a valid consumption reason.");
  const result = await supabase.rpc("consume_food_quantity", {
    p_food_item: id,
    p_quantity: quantity,
    p_reason: reason,
  });
  if (result.error) throw result.error;
}

export type ActivityEvent = {
  id: string;
  eventType: string;
  quantity: number | null;
  unit: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
};

export async function listFoodActivity(foodItemId: string): Promise<ActivityEvent[]> {
  const [linked, sourceMoves] = await Promise.all([
    supabase.from("activity_events")
    .select("id,event_type,quantity,unit,reason,metadata,created_at")
    .eq("item_reference_id", foodItemId)
    .order("created_at", { ascending: false }),
    supabase.from("activity_events")
      .select("id,event_type,quantity,unit,reason,metadata,created_at")
      .eq("event_type", "moved")
      .contains("metadata", { source_item_id: foodItemId })
      .order("created_at", { ascending: false }),
  ]);
  if (linked.error) throw linked.error;
  if (sourceMoves.error) throw sourceMoves.error;
  const events = [...(linked.data || []), ...(sourceMoves.data || [])]
    .filter((event, index, all) => all.findIndex((candidate) => candidate.id === event.id) === index)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return events.map((event) => ({
    id: event.id,
    eventType: event.event_type,
    quantity: event.quantity === null ? null : Number(event.quantity),
    unit: event.unit,
    reason: event.reason,
    metadata: (event.metadata || {}) as Record<string, unknown>,
    createdAt: event.created_at,
  }));
}
